/**
 * Files, everything the host and the room shared with the person and what they shared, and Search,
 * across what they can read and what was said in the replays.
 */
import { type ReactNode, useMemo, useState } from "react";
import { Face } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import type { TalkFile, TalkHit, TalkMoment, TalkSearchResponse } from "../../../shared/talk";
import { Frame } from "./Frame";
import { usePage } from "./context";
import { clock } from "./model";
import { DocPage, faceOf, FileTile, Head, Marked, Nav, useOpenFile } from "./Parts";
import { type FileFilter, fileBy, fileFrom, filterFiles, shortDate, weekOf } from "./talk";
import { useFeed } from "./useFeed";

const CHIPS: [FileFilter, string][] = [
  ["all", "All"],
  ["maya", "From Maya"],
  ["room", "From the room"],
  ["you", "Yours"],
];

// --- Files ---

export function FilesPlace(): ReactNode {
  const { token, host, lay, goPlace, nowMs } = usePage();
  const feed = useFeed("files", () => api.guestTalk.files(token), null);
  const open = useOpenFile();
  const [filter, setFilter] = useState<FileFilter>("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const all = feed.data?.files ?? [];
  const shown = useMemo(() => filterFiles(all, filter).filter((f) => f.name.toLowerCase().includes(q.trim().toLowerCase())), [all, filter, q]);
  const nowSec = Math.floor(nowMs / 1000);
  const count = (k: FileFilter) => filterFiles(all, k).length;
  const chipLabel = (k: FileFilter, label: string) => `${k === "maya" ? `From ${host.first}` : label}${count(k) ? ` ${count(k)}` : ""}`;
  const selected = shown.find((f) => f.key === picked) ?? shown[0] ?? null;
  const phone = lay === "phone";
  const wide = lay === "computer" || lay === "side";
  const weeks = (["This week", "Last week", "Earlier"] as const).map((w) => ({ w, list: shown.filter((f) => weekOf(f.sharedAt, nowSec) === w) })).filter((g) => g.list.length);

  const toChat = (f: TalkFile) => {
    const at = f.place;
    goPlace(!at || at.kind === "direct" ? { kind: "direct" } : { kind: at.kind, id: at.id });
  };

  const chips = (
    <span className={phone ? "gq-chips" : "jp-fchips"}>
      {CHIPS.map(([k, label]) => (
        <button key={k} type="button" className={filter === k ? "jp-fchip on" : "jp-fchip"} aria-pressed={filter === k} onClick={() => setFilter(k)}>
          {chipLabel(k, label)}
        </button>
      ))}
    </span>
  );
  const search = (
    <label className={phone ? "jp-q gq-q in" : "jp-fsearch"}>
      <Icon name="search" />
      <input placeholder="Search files" aria-label="Search files" value={q} onChange={(e) => setQ(e.target.value)} />
    </label>
  );
  const empty = feed.data && !shown.length ? <p className="jp-empty">{all.length ? "No file matches." : "Nothing has been shared yet."}</p> : null;

  const right = selected ? (
    <>
      <DocPage title={selected.name} by={host.name} />
      <div className="jp-fprev">
        <b>{selected.name}</b>
        <small>
          {fileBy(selected)} shared it in {selected.where} · {shortDate(selected.sharedAt)}
          {selected.size ? ` · ${selected.size}` : ""}
        </small>
      </div>
      <button type="button" className="jp-btn s" onClick={() => open(selected)}>
        <Icon name={selected.kind === "video" ? "playf" : "download"} /> {selected.kind === "video" ? "Play" : selected.kind === "link" ? "Open" : "Download"}
      </button>
      <button type="button" className="jp-btn soft s" onClick={() => toChat(selected)}>
        <Icon name="chat" /> Show in the chat
      </button>
    </>
  ) : null;

  if (phone)
    return (
      <Frame head={null} nav={<Nav title="Files" sub={`From ${host.first}, the room and you`} />}>
        <div className="gq-scroll">
          {search}
          {chips}
          {empty}
          {weeks.map(({ w, list }) => (
            <div key={w}>
              <p className="gq-h2">{w}</p>
              <section className="gq-list">
                {list.map((f) => (
                  <button key={f.key} type="button" className="gq-file" onClick={() => open(f)}>
                    <FileTile f={f} />
                    <span className="gq-row-t">
                      <b>{f.name}</b>
                      <small>
                        {fileBy(f)} · {f.where} · {shortDate(f.sharedAt)}
                      </small>
                    </span>
                    <span className="jp-frow-go">
                      <Icon name={f.kind === "video" ? "playf" : "download"} />
                    </span>
                  </button>
                ))}
              </section>
            </div>
          ))}
        </div>
      </Frame>
    );

  return (
    <Frame head={<Head icon="fold" title="Files" sub={`What ${host.first} and the room shared with you, and what you shared`} />} nav={null} right={right}>
      <div className="jp-fbar">
        {search}
        {chips}
      </div>
      <div className="jp-flist" aria-busy={!feed.data}>
        {empty}
        {weeks.map(({ w, list }) => (
          <div key={w}>
            <p className="jp-fweek">{w}</p>
            {list.map((f) => (
              <div
                key={f.key}
                className={wide && selected?.key === f.key ? "jp-frow on" : "jp-frow"}
                onClick={() => (wide ? setPicked(f.key) : open(f))}
                onKeyDown={(e) => e.key === "Enter" && (wide ? setPicked(f.key) : open(f))}
                role="button"
                tabIndex={0}
              >
                <FileTile f={f} />
                <span className="jp-frow-t">
                  <b>{f.name}</b>
                  <small>
                    {fileBy(f)} · {f.where} · {shortDate(f.sharedAt)}
                  </small>
                </span>
                {f.size ? <span className="jp-frow-s">{f.size}</span> : null}
                <button
                  type="button"
                  className="jp-frow-go"
                  aria-label={f.kind === "video" ? `Play ${f.name}` : `Open ${f.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    open(f);
                  }}
                >
                  <Icon name={f.kind === "video" ? "playf" : "download"} />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Frame>
  );
}

// --- Search ---

function MomentPic({ m }: { m: TalkMoment }): ReactNode {
  const faces = m.faces ?? [];
  return (
    <span className={faces.length ? "jp-rs-pic call" : "jp-rs-pic"}>
      {faces.length ? faces.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={i} src={f.photoUrl} alt="" /> : <span key={i} />)) : m.posterUrl ? <img src={m.posterUrl} alt="" /> : null}
      <i>{clock(m.atSec)}</i>
    </span>
  );
}

export function SearchPlace({ q }: { q: string }): ReactNode {
  const { token, host, lay, goPlace, playTalk } = usePage();
  const open = useOpenFile();
  const word = q.trim();
  const ask = word.length >= 2;
  const feed = useFeed<TalkSearchResponse | null>(`search:${word}`, () => (ask ? api.guestTalk.search(token, word) : Promise.resolve(null)), null);
  const d = ask ? feed.data : null;
  const [pickedAt, setPickedAt] = useState<number | null>(null);
  const moments = d?.moments ?? [];
  const messages = d?.messages ?? [];
  const files = d?.files ?? [];
  const total = moments.length + messages.length + files.length;
  const picked = moments[pickedAt ?? 0] ?? null;
  const phone = lay === "phone";
  const play = (m: TalkMoment) => playTalk(m.webinarId, { sessionId: m.liveSessionId ?? undefined, start: true, at: m.atSec });
  const go = (h: TalkHit) => goPlace(h.place.kind === "direct" ? { kind: "direct" } : { kind: h.place.kind, id: h.place.id });

  const results = (
    <>
      {!ask ? <p className="jp-empty">Type at least two letters.</p> : null}
      {d && total === 0 ? <p className="jp-empty">Nothing found for "{word}".</p> : null}
      {moments.length ? <p className={phone ? "gq-h2" : "jp-rs-h"}>Said in a replay · {moments.length}</p> : null}
      {moments.map((m, i) => (
        <button
          key={`${m.liveSessionId ?? m.webinarId}${m.atSec}`}
          type="button"
          className={phone ? "jp-rs-moment gq-moment" : i === (pickedAt ?? 0) ? "jp-rs-moment on" : "jp-rs-moment"}
          onClick={() => (lay === "computer" || lay === "side" ? setPickedAt(i) : play(m))}
        >
          <MomentPic m={m} />
          <span className="jp-rs-t">
            <b>{m.title}</b>
            <p>
              <Marked text={m.text} word={word} />
            </p>
          </span>
          {phone ? null : (
            <span
              className="jp-btn soft s jp-rs-play"
              role="presentation"
              onClick={(e) => {
                e.stopPropagation();
                play(m);
              }}
            >
              <Icon name="playf" /> {clock(m.atSec)}
            </span>
          )}
        </button>
      ))}
      {messages.length ? <p className={phone ? "gq-h2" : "jp-rs-h"}>Messages · {messages.length}</p> : null}
      {messages.map((h) => (
        <button key={h.id} type="button" className="jp-rs-msg" onClick={() => go(h)}>
          <Face {...faceOf(h.who)} size={phone ? 32 : 34} />
          <span className="jp-rs-t">
            <b>
              {h.who.kind === "member" && h.who.me ? "You" : h.who.name || "A member"}
              <small>
                {h.where} · {shortDate(h.createdAt)}
              </small>
            </b>
            <p>
              <Marked text={h.text} word={word} />
            </p>
          </span>
        </button>
      ))}
      {files.length ? <p className={phone ? "gq-h2" : "jp-rs-h"}>Files · {files.length}</p> : null}
      {files.map((f) => (
        <button key={f.key} type="button" className="jp-rs-msg" onClick={() => open(f)}>
          <FileTile f={f} />
          <span className="jp-rs-t">
            <b>
              <Marked text={f.name} word={word} />
              <small>
                {fileBy(f)} · {f.where} · {fileFrom(f) === "you" ? "yours" : shortDate(f.sharedAt)}
              </small>
            </b>
          </span>
        </button>
      ))}
    </>
  );

  const right = picked ? (
    <>
      <div className="jp-player">
        <MomentPlayerPic m={picked} />
        <span className="jp-player-bar">
          <i style={{ width: "14%" }} />
        </span>
        <b>{picked.title}</b>
        <small>
          Plays from {clock(picked.atSec)}, where {host.first} talks about it
        </small>
      </div>
      <button type="button" className="jp-btn s" onClick={() => play(picked)}>
        <Icon name="playf" /> Play from {clock(picked.atSec)}
      </button>
    </>
  ) : null;

  if (phone)
    return (
      <Frame head={null} nav={<Nav title="Search" />}>
        <div className="gq-scroll">
          <PhoneSearchBox q={q} />
          {results}
        </div>
      </Frame>
    );
  return (
    <Frame
      head={<Head icon="search" title={`"${word}"`} sub={d ? `${total} ${total === 1 ? "result" : "results"} in your conversations with ${host.first}, ${host.first}'s rooms and your circles` : "Messages, files and what was said in the replays"} />}
      nav={null}
      right={right}
    >
      <div className="jp-results" aria-busy={ask && !d}>
        {results}
      </div>
    </Frame>
  );
}

function MomentPlayerPic({ m }: { m: TalkMoment }): ReactNode {
  const faces = m.faces ?? [];
  return (
    <span className="jp-player-pic">
      {faces.length ? faces.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={i} src={f.photoUrl} alt="" /> : <span key={i} />)) : m.posterUrl ? <img src={m.posterUrl} alt="" /> : null}
      <Icon name="playf" />
    </span>
  );
}

/** A phone's search is typed on its own screen, since the left side is not there. */
function PhoneSearchBox({ q }: { q: string }): ReactNode {
  const { goPlace } = usePage();
  return (
    <label className={q ? "jp-q gq-q in on" : "jp-q gq-q in"}>
      <Icon name="search" />
      <input value={q} placeholder="Search messages and files" aria-label="Search messages and files" autoFocus onChange={(e) => goPlace({ kind: "search", q: e.target.value }, true)} />
      {q ? (
        <button type="button" aria-label="Clear the search" onClick={() => goPlace({ kind: "search", q: "" }, true)}>
          <Icon name="x" />
        </button>
      ) : null}
    </label>
  );
}
